export const PVK_POS_STORAGE_KEY = 'pouches-pvk-position-v1';

export interface PvkStoredPosition {
  /** 0–1 はビューポート幅に対する比率、それ以外は px */
  x: number;
  bottom: number;
}

export function measurePvkFooterClearancePx(): number {
  const footer = document.querySelector('.footer') as HTMLElement | null;
  if (!footer) {
    return 16;
  }
  return Math.ceil(footer.getBoundingClientRect().height) + 12;
}

export function readPvkStoredPosition(): PvkStoredPosition | null {
  try {
    const raw = localStorage.getItem(PVK_POS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PvkStoredPosition;
    if (typeof parsed.x === 'number' && typeof parsed.bottom === 'number') {
      return parsed;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function writePvkStoredPosition(pos: PvkStoredPosition): void {
  try {
    localStorage.setItem(PVK_POS_STORAGE_KEY, JSON.stringify(pos));
  } catch {
    /* ignore */
  }
}

export function clampPvkStoredPosition(pos: PvkStoredPosition): PvkStoredPosition {
  const minBottom = measurePvkFooterClearancePx();
  return {
    x: pos.x,
    bottom: Math.max(pos.bottom, minBottom),
  };
}

export function defaultPvkPosition(): PvkStoredPosition {
  return { x: 0.5, bottom: measurePvkFooterClearancePx() };
}

export function initialPvkPosition(): PvkStoredPosition {
  const stored = readPvkStoredPosition();
  if (stored) {
    return clampPvkStoredPosition(stored);
  }
  return defaultPvkPosition();
}

export function resolvePvkPixelX(pos: PvkStoredPosition, vw: number): number {
  return pos.x <= 1 ? pos.x * vw : pos.x;
}

/** パウチ／セルとキーボードの間に確保する最小余白（px） */
export const PVK_ANCHOR_CLEARANCE_PX = 44;

/** 下方向へずらすときの刻み（px） */
const PVK_BOTTOM_STEP_PX = 36;

function getPvkViewportSize(): { vw: number; vh: number } {
  const vv = window.visualViewport;
  return {
    vw: vv?.width ?? window.innerWidth,
    vh: vv?.height ?? window.innerHeight,
  };
}

interface PanelRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
  xPx: number;
  bottomPx: number;
}

function panelRectFromPosition(
  xPx: number,
  bottomPx: number,
  panelWidth: number,
  panelHeight: number,
  vw: number,
  vh: number,
  minBottom: number,
  margin: number,
): PanelRect {
  const halfW = panelWidth / 2;
  const clampedX = Math.max(halfW + margin, Math.min(vw - halfW - margin, xPx));
  const clampedBottom = Math.max(minBottom, Math.min(vh - panelHeight - margin, bottomPx));
  const top = vh - clampedBottom - panelHeight;
  const left = clampedX - halfW;
  return {
    left,
    top,
    right: left + panelWidth,
    bottom: top + panelHeight,
    xPx: clampedX,
    bottomPx: clampedBottom,
  };
}

function rectsOverlap(
  a: { left: number; top: number; right: number; bottom: number },
  b: PanelRect,
  margin: number,
): boolean {
  return (
    a.left < b.right + margin &&
    a.right > b.left - margin &&
    a.top < b.bottom + margin &&
    a.bottom > b.top - margin
  );
}

function toStoredPosition(pr: PanelRect, vw: number): PvkStoredPosition {
  return {
    x: pr.xPx / vw,
    bottom: pr.bottomPx,
  };
}

/** 編集対象の近くにキーボードが被らないよう出現位置を決める（手動ドラッグ位置は localStorage に保存しない） */
export function autoAdjustPvkPositionAvoidingAnchor(
  anchorRect: DOMRect,
  panelWidth: number,
  panelHeight: number,
  preferred: PvkStoredPosition,
  minBottom = measurePvkFooterClearancePx(),
  margin = PVK_ANCHOR_CLEARANCE_PX,
): PvkStoredPosition {
  const { vw, vh } = getPvkViewportSize();
  if (panelWidth <= 0 || panelHeight <= 0 || vw <= 0 || vh <= 0) {
    return clampPvkStoredPosition(preferred);
  }

  const anchor = {
    left: anchorRect.left,
    top: anchorRect.top,
    right: anchorRect.right,
    bottom: anchorRect.bottom,
  };
  const anchorCenterX = anchor.left + anchorRect.width / 2;
  const anchorCenterY = anchor.top + anchorRect.height / 2;
  const preferAbove = anchorCenterY > vh * 0.42;

  const prefX = resolvePvkPixelX(preferred, vw);
  const maxBottom = vh - panelHeight - margin;
  const aboveBottom = vh - anchor.top + margin;

  const candidates: Array<{ xPx: number; bottomPx: number }> = [];

  if (preferAbove) {
    candidates.push(
      { xPx: anchorCenterX, bottomPx: aboveBottom },
      { xPx: prefX, bottomPx: aboveBottom },
      { xPx: anchorCenterX, bottomPx: maxBottom },
      { xPx: prefX, bottomPx: maxBottom },
    );
  }

  candidates.push(
    { xPx: prefX, bottomPx: preferred.bottom },
    { xPx: anchorCenterX, bottomPx: preferred.bottom },
  );

  for (let stepBottom = preferred.bottom + PVK_BOTTOM_STEP_PX; stepBottom <= maxBottom; stepBottom += PVK_BOTTOM_STEP_PX) {
    candidates.push({ xPx: prefX, bottomPx: stepBottom });
    candidates.push({ xPx: anchorCenterX, bottomPx: stepBottom });
  }

  if (!preferAbove) {
    candidates.push(
      { xPx: anchorCenterX, bottomPx: aboveBottom },
      { xPx: prefX, bottomPx: aboveBottom },
    );
  }

  candidates.push(
    { xPx: anchorCenterX, bottomPx: maxBottom },
    { xPx: prefX, bottomPx: maxBottom },
  );

  for (const cand of candidates) {
    const pr = panelRectFromPosition(
      cand.xPx,
      cand.bottomPx,
      panelWidth,
      panelHeight,
      vw,
      vh,
      minBottom,
      margin,
    );
    if (!rectsOverlap(anchor, pr, margin)) {
      return toStoredPosition(pr, vw);
    }
  }

  return toStoredPosition(
    panelRectFromPosition(
      anchorCenterX,
      maxBottom,
      panelWidth,
      panelHeight,
      vw,
      vh,
      minBottom,
      margin,
    ),
    vw,
  );
}

/** 値編集中 input / テーブルセル / パウチ本体からアンカー要素を取得 */
export function findPvkKeyboardAnchorElement(pouchId?: string): HTMLElement | null {
  const active = document.activeElement;
  if (active instanceof HTMLElement) {
    const tableCell = active.closest('.table-cell-edit-shell');
    if (tableCell instanceof HTMLElement) {
      return tableCell;
    }
    const pouch = active.closest('.pouch-container');
    if (pouch instanceof HTMLElement) {
      const circle = pouch.querySelector('.pouch-main-circle');
      return (circle instanceof HTMLElement ? circle : pouch);
    }
  }

  if (pouchId) {
    const pouch = document.querySelector(`[data-pouch-id="${CSS.escape(pouchId)}"]`);
    if (pouch instanceof HTMLElement) {
      const circle = pouch.querySelector('.pouch-main-circle');
      return (circle instanceof HTMLElement ? circle : pouch);
    }
  }

  return null;
}
